/**
 * THE SERVER'S OPEN ITEMS ON THE SCREEN (C1-d, paged mode only).
 *
 * `GET /{id}/open-items` derives the same open work approve gates on, including the
 * C1-c "verdict data missing" check, and sends it per issue with a COUNT and a DIGEST
 * of the articles, never the list. In paged mode the footer count, the open-points
 * map, Finish, Weiter and the navigator's ✓ read that list for every regulation except
 * the one the reviewer has open: that one keeps the browser's own derivation, so a
 * click never waits for the server. The two agree once `/open-items` refetched —
 * the shared `flowParity` fixture pins the server's derivation to the browser's.
 *
 * Three pure pieces, so the rule is testable without a query client:
 *   - `openItemsToWorklist`: the wire issue in the screen's shape (`articleIds` empty,
 *     `articleCount` from the server);
 *   - `mergeWorklist`: server issues of the other regulations + the browser's issues of
 *     the open one;
 *   - `openItemsStatus`: whether the server's answer may be shown as settled.
 */
import type { DeclarationOpenItemsWire } from "../../../../api/types"
import type { OpenPointTarget } from "../studio-v2/footer/nextOpenPoint"
import type { WorklistIssue } from "./cellState"

/** A worklist issue that came from the server: its articles are only counted. A
 *  reader that needs the article list of a NON-ACTIVE regulation loads that
 *  regulation (C3-b3, `loadFrameworkState`); none may read `articleIds` off this. */
export interface ServerWorklistIssue extends WorklistIssue {
  articleCount?: number
}

/** The wire issue in the screen's shape. Order kept (the server sends blocking first,
 *  then let-through; `mergeWorklist` re-sorts structurally anyway). */
export function openItemsToWorklist(
  out: DeclarationOpenItemsWire,
): ServerWorklistIssue[] {
  return out.issues.map((i) => {
    const issue: ServerWorklistIssue = {
      unit: i.unit,
      unitId: i.unit_id,
      framework: i.framework,
      step: i.step,
      state: i.state as WorklistIssue["state"],
      articleIds: [],
      articleCount: i.article_count,
    }
    if (i.statement_id != null) issue.statementId = i.statement_id
    if (i.group_index != null) issue.groupIndex = i.group_index
    return issue
  })
}

/** A backend older than C3-s answers 200 without `frameworks` (or with null): the page
 *  then keeps today's browser derivation for its whole mount. `[]` is a real answer
 *  (no statement yet), not an old backend. */
export function isOldOpenItemsBackend(out: DeclarationOpenItemsWire): boolean {
  return out.frameworks == null
}

/** `deriveWorklist`'s own order (`cellState.ts`), structural and state-free: step,
 *  the framework unit before statements, framework slug, group index. */
function structuralOrder(a: WorklistIssue, b: WorklistIssue): number {
  const unitRank = (u: WorklistIssue["unit"]) => (u === "framework" ? 0 : 1)
  return (
    a.step - b.step ||
    unitRank(a.unit) - unitRank(b.unit) ||
    a.framework.localeCompare(b.framework) ||
    (a.groupIndex ?? -1) - (b.groupIndex ?? -1)
  )
}

export interface MergeWorklistInput {
  /** The server's issues (`openItemsToWorklist`). */
  server: readonly ServerWorklistIssue[]
  /** The regulation the reviewer has open; null when none (no regulation yet). */
  activeFramework: string | null
  /** The browser's own worklist; only its issues of `activeFramework` are used. */
  browser: readonly WorklistIssue[]
}

/**
 * The worklist the paged screen shows: the server's issues for every regulation but
 * the open one, the browser's issues for the open one.
 *
 * NO SESSION READY MARKS. `useFlowSession().pendingIssues` has no production writer
 * today, so nothing is laid over the server's regulations. A future mark writer must
 * add the overlay back with the browser's own rule: a mark shows only on a CONFIRMED
 * unit (a step-3 mark only on a statement with articles). The server's "no open issue"
 * cannot tell confirmed from "nothing to judge", so the overlay needs that fact from
 * the summary first.
 */
export function mergeWorklist(
  input: MergeWorklistInput,
): ServerWorklistIssue[] {
  const { server, activeFramework, browser } = input
  return [
    ...server.filter((i) => i.framework !== activeFramework),
    ...browser.filter((i) => i.framework === activeFramework),
  ].sort(structuralOrder)
}

/**
 * Whether the server's answer may be shown as settled:
 *   - "fresh": the last answer landed and nothing is refetching;
 *   - "refreshing": an answer is shown and a newer one is on its way (after a
 *     gesture: NULL-framework condition rows and a growing pool can move other
 *     regulations too). The ✓ keep the last answer, so nothing flickers on every
 *     save; Finish waits for the new one (`approveDisabled`, beside saving);
 *   - "pending": no answer yet — the other regulations' ✓ show as pending and
 *     Finish is blocked;
 *   - "unknown": the read failed (after its retries) — the other regulations' ✓
 *     show as unknown and Finish stays blocked. Never "all done" from a missing
 *     answer.
 */
export type OpenItemsStatus = "fresh" | "refreshing" | "pending" | "unknown"

export function openItemsStatus(q: {
  hasData: boolean
  isError: boolean
  isFetching: boolean
}): OpenItemsStatus {
  if (q.isError) return "unknown"
  if (!q.hasData) return "pending"
  return q.isFetching ? "refreshing" : "fresh"
}

/** The server's answer is missing ("pending") or failed ("unknown"): the other
 *  regulations' ✓ and Finish cannot be trusted. "refreshing" keeps the last answer. */
export function openItemsUnsettled(
  status: OpenItemsStatus | null | undefined,
): status is "pending" | "unknown" {
  return status === "pending" || status === "unknown"
}

/** Weiter on a missing or failed server answer: the browser only knows the open
 *  regulation, so "nothing left" would point at an Approve that stays disabled.
 *  Stay on the current card instead (null = do not move). */
export function weiterTarget(
  target: OpenPointTarget,
  status: OpenItemsStatus | null | undefined,
): OpenPointTarget | null {
  return target.kind === "approve" && openItemsUnsettled(status) ? null : target
}
